import {
  BackofficeActivitiesOrderItem,
  BackofficeActivitiesPromos,
  BackofficeOrderDetail,
} from '@diamond/shared/types';

type OrderItemWithProductCode = BackofficeOrderDetail['order_items'][number] & {
  product_item_code?: number | string;
};

type BuildActivityOrderItemsOptions = {
  injectPromoFreeGoods: boolean;
};

const attachProductItemCode = (
  item: BackofficeOrderDetail['order_items'][number]
): OrderItemWithProductCode => ({
  ...item,
  product_item_code:
    (item as { product_item_code?: number | string }).product_item_code ??
    (item as { item_code?: number }).item_code ??
    '',
});

const isFreeGoodRewardRow = (item: { quantity_free_goods?: number | null }) =>
  Boolean(item.quantity_free_goods && Number(item.quantity_free_goods) > 0);

const mergeFreeGoodPromo = <
  T extends OrderItemWithProductCode & { product_id: string }
>(
  item: T,
  match: NonNullable<
    BackofficeActivitiesPromos['single_free_good_promos']
  >[number]
): T => ({
  ...item,
  ...match,
  product_item_code: match.product_item_code ?? item.product_item_code ?? '',
  free_goods_product_item_code:
    match.free_goods_product_item_code ?? match.product_item_code,
});

const injectPromoFreeGoodRows = <T extends OrderItemWithProductCode>(
  items: T[],
  promos: BackofficeActivitiesPromos
): T[] => {
  const withSingleFreeGoods = items.flatMap((item) => {
    const match = promos.single_free_good_promos.find(
      (freeGood) => freeGood.ref_product_id === item.product_id
    );

    if (match) {
      return [item, mergeFreeGoodPromo(item, match)];
    }

    return [item];
  });

  return withSingleFreeGoods.flatMap((item) => {
    const match = promos.bundled_free_good_promos.find(
      (freeGood) => freeGood.ref_product_id === item.product_id
    );

    if (match) {
      return [item, mergeFreeGoodPromo(item, match)];
    }

    return [item];
  });
};

const attachManualProcessThresholds = <
  T extends OrderItemWithProductCode & { item_code: number }
>(
  items: T[],
  manualProcessItems: BackofficeOrderDetail['order']['manual_process_items']
): T[] => {
  const manualProcessMap = new Map<number, number>();

  if (manualProcessItems) {
    manualProcessItems.forEach((manualProcessItem) => {
      manualProcessMap.set(
        Number(manualProcessItem.product_item_code),
        manualProcessItem.threshold_quantity
      );
    });
  }

  return items.map((item) => {
    const threshold = manualProcessMap.get(item.item_code);

    if (threshold !== undefined) {
      return { ...item, threshold_quantity: threshold };
    }

    return item;
  });
};

export function mergeOrderItemsWithPromos<
  T extends OrderItemWithProductCode & { product_id: string; item_code: number }
>(
  items: T[],
  promos: BackofficeActivitiesPromos | null | undefined,
  options: BuildActivityOrderItemsOptions
): T[] {
  if (!promos) {
    return items;
  }

  const withDiscount = items.map((item) => {
    const match = promos.compiled_product_discounts.find(
      (discount) => discount.product_id === item.product_id
    );
    const itemWithProductCode = attachProductItemCode(item);

    return match
      ? { ...itemWithProductCode, ...match }
      : itemWithProductCode;
  });

  const mergedItems = options.injectPromoFreeGoods
    ? injectPromoFreeGoodRows(withDiscount, promos)
    : withDiscount.filter((item) => !isFreeGoodRewardRow(item));

  return mergedItems;
}

export function buildActivityOrderItems(
  detail: BackofficeOrderDetail,
  options: BuildActivityOrderItemsOptions
): BackofficeActivitiesOrderItem[] {
  const mergedItems = mergeOrderItemsWithPromos(
    detail.order_items.map(attachProductItemCode),
    detail.order.promos,
    options
  );

  return attachManualProcessThresholds(
    mergedItems,
    detail.order.manual_process_items
  ) as BackofficeActivitiesOrderItem[];
}
